import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';

interface Product {
  id: string;
  name: string;
  brand: string;
  rating: number;
  reviews_count: number;
  price: number;
  original_price: number | null;
  image: string;
  badge: string;
}

function Stars({ rating }: { rating: number }) {
  const full = Math.floor(rating || 0);
  const half = (rating || 0) - full >= 0.5;
  return (
    <span className="flex items-center gap-0.5 text-amber-400">
      {Array.from({ length: 5 }).map((_, i) => {
        const cls = i < full ? 'ri-star-fill' : i === full && half ? 'ri-star-half-line' : 'ri-star-line';
        return <i key={i} className={`${cls} text-xs`}></i>;
      })}
    </span>
  );
}

export default function EditorsPicks() {
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('products')
      .select('id,name,brand,rating,reviews_count,price,original_price,image,badge')
      .order('rating', { ascending: false })
      .limit(8)
      .then(({ data, error: err }) => {
        if (cancelled) return;
        if (err) setError(true);
        else if (data) setProducts(data);
        setLoading(false);
      })
      .catch(() => { if (!cancelled) { setError(true); setLoading(false); } });
    return () => { cancelled = true; };
  }, []);

  if (loading) return (
    <section className="py-16 bg-white">
      <div className="max-w-7xl mx-auto px-4 md:px-6 text-center text-ink-400 text-sm">Loading...</div>
    </section>
  );

  if (error || products.length === 0) return null;

  return (
    <section id="editors-picks" className="py-16 md:py-20 bg-white border-b border-ink-100 reveal">
      <div className="max-w-7xl mx-auto px-4 md:px-6">
        <div className="flex items-end justify-between gap-6 mb-8">
          <div>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-ink-900 tracking-tight">
              Editor&rsquo;s picks
            </h2>
            <p className="mt-1.5 text-ink-500 text-sm max-w-xl">
              Rigorously tested by our team. No sponsored placements. Just products we actually stand behind.
            </p>
          </div>
          <Link
            to="/editors-picks"
            className="hidden md:inline-flex items-center gap-1 text-sm font-semibold text-ink-600 hover:text-cta-600 transition whitespace-nowrap"
          >
            See all picks <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {products.map((p, i) => (
            <div key={p.id} className="group bg-white rounded-xl border border-ink-100 overflow-hidden hover:shadow-card hover:-translate-y-0.5 transition duration-200">
              <Link to={`/product/${p.id}`} className="relative aspect-[4/3] bg-ink-50 overflow-hidden block">
                <img
                  src={p.image}
                  alt={p.name}
                  className="w-full h-full object-contain p-4 group-hover:scale-105 transition duration-500"
                />
                {i === 0 && (
                  <span className="absolute top-3 left-3 h-6 px-2 rounded-md bg-cta-500 text-white text-[10px] font-bold uppercase tracking-wider flex items-center gap-1">
                    <i className="ri-award-line text-xs"></i> Top Pick
                  </span>
                )}
              </Link>
              <div className="p-4">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-ink-400">{p.brand}</p>
                <h3 className="mt-1 font-semibold text-ink-900 text-sm leading-snug line-clamp-2">
                  <Link to={`/product/${p.id}`} className="hover:text-cta-600 transition">{p.name}</Link>
                </h3>
                <div className="mt-2 flex items-center gap-2">
                  <Stars rating={p.rating} />
                  <span className="text-xs text-ink-500">({(p.reviews_count || 0).toLocaleString()})</span>
                </div>
                <div className="mt-3 flex items-end justify-between">
                  <div>
                    <p className="font-display text-lg font-bold text-ink-900">${p.price}</p>
                    {p.original_price != null && p.original_price > p.price && (
                      <p className="text-xs text-ink-400 line-through">${p.original_price}</p>
                    )}
                  </div>
                  <Link
                    to={`/product/${p.id}`}
                    className="h-8 px-3 rounded-lg border border-ink-200 hover:border-cta-400 hover:text-cta-600 text-xs font-semibold text-ink-700 whitespace-nowrap flex items-center gap-1 transition"
                  >
                    Review <i className="ri-arrow-right-line text-xs"></i>
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-8 text-center md:hidden">
          <Link
            to="/editors-picks"
            className="inline-flex items-center gap-1.5 text-sm font-semibold text-cta-600 hover:text-cta-700 transition"
          >
            See all editor picks <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
